import { FormEvent, ReactNode, useId } from 'react';
import { ToastView, Toast } from '../promotions/useToast';

/**
 * #1113 — the building blocks of the Finance config cards (approved mock v2): the card frame
 * (title, status, Unsaved pill, last change + History, footer with Discard / Save changes), an
 * on/off switch, and a number field with its unit, hint, deployed-value note and inline error.
 */

export function ConfigCardFrame({
  testId,
  title,
  status,
  dirty,
  sub,
  lastChanged,
  onHistory,
  footNote,
  footError,
  invalidCount,
  saving,
  canSave,
  canDiscard,
  onDiscard,
  onSubmit,
  toast,
  children,
}: {
  testId: string;
  title: string;
  status?: ReactNode;
  dirty: boolean;
  sub: ReactNode;
  lastChanged: string;
  onHistory: () => void;
  footNote: string;
  footError?: ReactNode;
  invalidCount: number;
  saving: boolean;
  canSave: boolean;
  /** Defaults to `dirty`; a card with local-only edits (e.g. the fee type) can allow Discard too. */
  canDiscard?: boolean;
  onDiscard: () => void;
  onSubmit: () => void;
  toast: Toast | null;
  children: ReactNode;
}) {
  const titleId = useId();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit();
  };
  return (
    <form className="admin-card config-card" data-testid={testId} noValidate aria-labelledby={titleId} onSubmit={submit}>
      <div className="config-card-header">
        <div>
          <h3 className="admin-card-title config-card-title" id={titleId}>
            {title}
            {status}
            {dirty && <span className="admin-badge unsaved" data-testid={`${testId}-unsaved`}>Unsaved</span>}
          </h3>
          <div className="config-card-sub">{sub}</div>
        </div>
        <div className="config-card-meta">
          <span data-testid={`${testId}-last-changed`}>{lastChanged}</span>
          <button type="button" className="config-link-btn" onClick={onHistory}>History</button>
        </div>
      </div>

      {children}

      <div className="config-card-foot">
        <span className="config-foot-note">{footNote}</span>
        <div className="config-foot-actions">
          {footError && <span className="config-foot-err" role="alert" data-testid={`${testId}-error`}>{footError}</span>}
          {!footError && invalidCount > 0 && (
            <span className="config-foot-err" role="alert">
              Fix {invalidCount} field{invalidCount > 1 ? 's' : ''} to save
            </span>
          )}
          <button type="button" className="admin-btn admin-btn-outline" onClick={onDiscard} disabled={!(canDiscard ?? dirty) || saving}>
            Discard
          </button>
          <button type="submit" className="admin-btn admin-btn-primary" disabled={!canSave || saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
      <ToastView toast={toast} />
    </form>
  );
}

export function ConfigSwitch({
  id,
  checked,
  onChange,
  label,
  description,
  disabled,
}: {
  id: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  label: ReactNode;
  description: ReactNode;
  disabled?: boolean;
}) {
  return (
    <div className="config-switch-row">
      <div className="config-switch-wrap">
        <button
          type="button"
          role="switch"
          id={id}
          className="config-switch"
          aria-checked={checked}
          aria-labelledby={`${id}-label`}
          aria-describedby={`${id}-desc`}
          disabled={disabled}
          onClick={() => onChange(!checked)}
        />
        <span className="config-switch-state" aria-hidden="true">{checked ? 'On' : 'Off'}</span>
      </div>
      <div className="config-switch-text">
        <div className="config-switch-label" id={`${id}-label`}>{label}</div>
        <div className="config-hint" id={`${id}-desc`}>{description}</div>
      </div>
    </div>
  );
}

export function NumberField({
  id,
  label,
  unit,
  unitBefore,
  value,
  onChange,
  hint,
  error,
  deployed,
  disabled,
}: {
  id: string;
  label: string;
  unit: string;
  unitBefore?: boolean;
  value: string;
  onChange: (v: string) => void;
  hint: string;
  error?: string | null;
  /** True when the key has no override: the field shows the deployed value. */
  deployed?: boolean;
  disabled?: boolean;
}) {
  const unitEl = <span className={`config-unit ${unitBefore ? 'before' : 'after'}`} aria-hidden="true">{unit}</span>;
  return (
    <div className={`config-field${error ? ' invalid' : ''}`} data-testid={`field-${id}`}>
      <label htmlFor={id}>{label}{unit ? <span className="config-sr-only"> ({unit})</span> : null}</label>
      <div className="config-affix">
        {unitBefore && unitEl}
        <input
          id={id}
          inputMode="decimal"
          autoComplete="off"
          value={value}
          disabled={disabled}
          aria-invalid={error ? true : false}
          aria-describedby={`${id}-hint${error ? ` ${id}-err` : ''}`}
          onChange={(e) => onChange(e.target.value)}
        />
        {!unitBefore && unitEl}
      </div>
      <div className="config-hint" id={`${id}-hint`}>
        {hint}
        {deployed && <span className="config-deployed"> Using the deployed value.</span>}
      </div>
      {error && <div className="config-err" id={`${id}-err`}>{error}</div>}
    </div>
  );
}

/** The red banner + Reload a card shows when the admin lost the screen mid-session (design §4). */
export function ForbiddenNotice({ onReload }: { onReload: () => void }) {
  return (
    <div className="admin-info-banner danger config-banner" role="alert" data-testid="config-forbidden">
      <span>You no longer have permission to change these settings.</span>
      <button type="button" className="admin-btn admin-btn-sm admin-btn-outline" onClick={onReload}>Reload</button>
    </div>
  );
}

/** Loading / error / missing-key states a card renders in place of its fields. */
export function CardStateFrame({ testId, title, children }: { testId: string; title: string; children: ReactNode }) {
  return (
    <div className="admin-card config-card" data-testid={testId}>
      <div className="config-card-header">
        <h3 className="admin-card-title config-card-title">{title}</h3>
      </div>
      {children}
    </div>
  );
}

export const AED = new Intl.NumberFormat('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const money = (n: number) => AED.format(n);
